import { useEffect, useRef, useState } from "react";
import { Button, Input, Switch, TextArea } from "@akin2unde/flowui-react";
import type { Role } from "../../models/Role";
import type { SystemUser } from "../../models/SystemUser";
import type { InventoryWareHouse } from "../../models/InventoryWareHouse";
import { RoleService } from "../../services/RoleService";
import { UserService } from "../../services/UserService";
import { InventoryWareHouseService } from "../../services/InventoryWareHouseService";
import { loadAllLookupPages } from "../../hooks/useInventoryLookups";
import { RoleUiCatalogue, actionLabel } from "../../access/RoleUiCatalogue";
import { DataState } from "../../models/DataState";
import { useAuth } from "../../auth/useAuth";
import "./RoleCss.css";
export default function RoleHTML() {
  const [roles, setRoles] = useState<Role[]>([]),
    [selected, setSelected] = useState<Role>(),
    [tab, setTab] = useState<"info" | "users">("info"),
    [members, setMembers] = useState<SystemUser[]>([]),
    [warehouses, setWarehouses] = useState<InventoryWareHouse[]>([]),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [loading, setLoading] = useState(true),
    [saving, setSaving] = useState(false),
    [membersLoading, setMembersLoading] = useState(false),
    [membersError, setMembersError] = useState(""),
    [dirty, setDirty] = useState(false);
  const busy = useRef(false);
  const { hasPermission, state } = useAuth();
  const canCreate =
      hasPermission("Role.Create") || (state.RoleUis === undefined && hasPermission("Role.Manage")),
    canEdit = hasPermission("Role.Edit") || (state.RoleUis === undefined && hasPermission("Role.Manage"));
  const editable = selected?.Code ? canEdit : canCreate;
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [r, w] = await Promise.all([
        loadAllLookupPages(RoleService().GetAll),
        loadAllLookupPages(InventoryWareHouseService().GetAll),
      ]);
      setRoles(r);
      setWarehouses(w);
      setSelected((x) => x ?? r[0]);
    } catch {
      setError("Could not load roles and warehouses. Retry.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    let alive = true;
    setMembers([]);
    setMembersError("");
    if (tab !== "users" || !selected?.Code) return;
    if (!hasPermission("User.View")) {
      setMembersError("You need user viewing access to see role members.");
      return;
    }
    setMembersLoading(true);
    void loadAllLookupPages(UserService().GetAll)
      .then((users) => {
        if (alive)
          setMembers(users.filter((u) => u.RoleCodes?.includes(selected.Code)));
      })
      .catch(() => {
        if (alive)
          setMembersError(
            "Could not load users in this role. Switch tabs to retry.",
          );
      })
      .finally(() => {
        if (alive) setMembersLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [tab, selected?.Code, hasPermission]);
  const update = (patch: Partial<Role>) => {
    setSelected((x) => (x ? { ...x, ...patch } : x));
    setDirty(true);
    setMessage("");
  };
  const pick = (role: Role) => {
    if (dirty && !window.confirm("Discard unsaved role changes?")) return;
    setSelected({
      ...role,
      RoleUis:
        role.RoleUis?.map((ui) => ({ ...ui, Actions: [...ui.Actions] })) ?? [],
    });
    setDirty(false);
    setTab("info");
    setMessage("");
    setError("");
  };
  const save = async () => {
    if (!selected || busy.current || !editable) return;
    if (!selected.Name.trim() || !selected.WareHouse) {
      setError("Enter a role name and select its warehouse.");
      return;
    }
    busy.current = true;
    setSaving(true);
    setError("");
    try {
      const saved = (
        await RoleService().Save([
          {
            ...selected,
            Name: selected.Name.trim(),
            RoleUis: selected.RoleUis?.map(grant=>({...grant,Name:RoleUiCatalogue.find(ui=>ui.Code===grant.Code)?.Name ?? grant.Name ?? grant.Code,RoleCode:selected.Code || ""})),
            DataState: selected.Code ? DataState.Changed : DataState.New,
          },
        ])
      )[0];
      if (!saved?.Code) throw Error("No saved role code was returned.");
      setRoles((x) => [...x.filter((r) => r.Code !== saved.Code), saved]);
      setSelected(saved);
      setDirty(false);
      if (saved.RoleUis === undefined) {
        setError(
          "Basic role details were saved, but this backend does not return RoleUis. Apply the included backend role additions before relying on page/action selections.",
        );
      } else setMessage("Role saved successfully.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save role.");
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };
  const uis = selected?.RoleUis ?? [];
  return (
    <section className="role-page">
      <header className="role-page-heading">
        <div>
          <h1>Application roles</h1>
          <p>Choose pages and actions, and view users assigned to a role.</p>
        </div>
        {canCreate && (
          <Button
            disabled={saving || loading}
            onClick={() =>
              pick({
                Code: "",
                Name: "",
                Description: "",
                IsActive: true,
                IsSystemRole: false,
                WareHouse: "",
                RoleUis: [],
              })
            }
          >
            New role
          </Button>
        )}
      </header>
      {state.User?.IsTenantAdmin && (
        <p className="role-admin-note">
          Tenant admins have full access to all pages, actions and warehouses
          within their tenant.
        </p>
      )}
      {loading ? (
        <p role="status">Loading roles…</p>
      ) : (
        <div className="role-workspace">
          <aside className="role-list">
            {roles.map((role) => (
              <button
                key={role.Code}
                disabled={saving}
                aria-pressed={selected?.Code === role.Code}
                onClick={() => pick(role)}
              >
                {role.Name}
                <small>{role.IsActive ? "Active" : "Inactive"}</small>
              </button>
            ))}
            {!roles.length && <p>No roles yet.</p>}
          </aside>
          {selected && (
            <article className="role-editor">
              <h2>{selected.Name || "New role"}</h2>
              <div
                className="role-tabs"
                role="tablist"
                aria-label="Role details"
              >
                <button
                  role="tab"
                  aria-selected={tab === "info"}
                  onClick={() => setTab("info")}
                >
                  Role info
                </button>
                <button
                  role="tab"
                  aria-selected={tab === "users"}
                  disabled={!selected.Code}
                  onClick={() => setTab("users")}
                >
                  Users in role
                </button>
              </div>
              {tab === "info" ? (
                <>
                  <div className="role-fields">
                    <label>
                      Role name
                      <Input
                        value={selected.Name}
                        disabled={!editable || saving}
                        onChange={(e) => update({ Name: e.target.value })}
                      />
                    </label>
                    <label>
                      Description
                      <TextArea
                        value={selected.Description ?? ""}
                        disabled={!editable || saving}
                        onChange={(e) =>
                          update({ Description: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      Warehouse
                      <select
                        value={selected.WareHouse ?? ""}
                        disabled={!editable || saving}
                        onChange={(e) => update({ WareHouse: e.target.value })}
                      >
                        <option value="">Select warehouse</option>
                        {warehouses.map((w) => (
                          <option value={w.Code} key={w.Code}>
                            {w.Name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="role-inline">
                      Active
                      <Switch
                        checked={selected.IsActive}
                        disabled={!editable || saving}
                        onChange={(checked) => update({ IsActive: checked })}
                      />
                    </label>
                    <p>System role: {selected.IsSystemRole ? "Yes" : "No"}</p>
                  </div>
                  <div className="role-access">
                    <h3>Pages &amp; actions</h3>
                    <p>Selected pages without actions give view-only access.</p>
                    {RoleUiCatalogue.map((ui) => {
                      const grant = uis.find((x) => x.Code === ui.Code);
                      return (
                        <div className="role-page-access" key={ui.Code}>
                          <label className="role-inline">
                            <input
                              type="checkbox"
                              checked={!!grant}
                              disabled={!editable || saving}
                              onChange={(e) =>
                                update({
                                  RoleUis: e.target.checked
                                    ? [...uis, { Code: ui.Code, Actions: [] }]
                                    : uis.filter((x) => x.Code !== ui.Code),
                                })
                              }
                            />
                            {ui.Name}
                          </label>
                          <div className="role-action-options">
                            {ui.Actions.map((action) => (
                              <label key={action}>
                                <input
                                  type="checkbox"
                                  disabled={!editable || saving || !grant}
                                  checked={
                                    grant?.Actions.includes(action) ?? false
                                  }
                                  onChange={(e) =>
                                    update({
                                      RoleUis: uis.map((x) =>
                                        x.Code === ui.Code
                                          ? {
                                              ...x,
                                              Actions: e.target.checked
                                                ? [...x.Actions, action]
                                                : x.Actions.filter(
                                                    (a) => a !== action,
                                                  ),
                                            }
                                          : x,
                                      ),
                                    })
                                  }
                                />
                                {actionLabel(action)}
                              </label>
                            ))}
                            {!!grant && !!ui.Actions.length && editable && (
                              <button
                                disabled={saving}
                                onClick={() =>
                                  update({
                                    RoleUis: uis.map((x) =>
                                      x.Code === ui.Code
                                        ? { ...x, Actions: [...ui.Actions] }
                                        : x,
                                    ),
                                  })
                                }
                              >
                                Select all actions
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <footer>
                    <Button
                      variant="outline"
                      disabled={saving}
                      onClick={() => {
                        const original = roles.find(
                          (r) => r.Code === selected.Code,
                        );
                        setSelected(original);
                        setDirty(false);
                      }}
                    >
                      Cancel
                    </Button>
                    {editable && (
                      <Button onClick={() => void save()} disabled={saving}>
                        {saving ? "Saving…" : "Save role"}
                      </Button>
                    )}
                  </footer>
                </>
              ) : (
                <div className="role-members">
                  <p>Read-only. Manage role assignments on the Users page.</p>
                  {membersLoading ? (
                    <p role="status">Loading role users…</p>
                  ) : membersError ? (
                    <p role="alert">{membersError}</p>
                  ) : members.length ? (
                    <div className="role-members-table">
                      <table>
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Warehouses</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((u) => (
                            <tr key={u.Code}>
                              <td>
                                {u.FirstName} {u.LastName}
                                <small>{u.Email}</small>
                              </td>
                              <td>
                                {u.WareHouses?.map(
                                  (c) =>
                                    warehouses.find((w) => w.Code === c)
                                      ?.Name ?? c,
                                ).join(", ") || "None"}
                              </td>
                              <td>{u.IsActive ? "Active" : "Inactive"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p>No users are assigned to this role.</p>
                  )}
                </div>
              )}
            </article>
          )}
        </div>
      )}
      {error && (
        <div role="alert">
          <p>{error}</p>
          {loading === false && !selected && (
            <Button onClick={() => void load()}>Retry</Button>
          )}
        </div>
      )}
      {message && (
        <p role="status" className="success-message-text">
          {message}
        </p>
      )}
    </section>
  );
}
